import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import authUtils from "../authUtils";
import * as jwtUtils from "../jwtUtils";
import { AuthContext } from "../AuthContext";

const getAndRemoveTokenFromQueryParam = () => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token");
    url.searchParams.delete('token');
    window.history.replaceState(null, '', url.toString());
    return token;
}

export const AuthProvider = ({loginUrl, appId, onLogin, children}) => {
    const [token, setToken] = useState(null);
    const [data, setData] = useState(null);
    let invalidateSessionTimeout;

    useEffect(() => {
        const token = getAndRemoveTokenFromQueryParam();
        _setToken(token || authUtils.getToken());
        return () => {
            clearTimeout(invalidateSessionTimeout);
        };
    }, []);

    /**
     * Set new token and trigger callback
     * @param token
     * @private
     */
    const _setToken = (token) => {
        // Check if token
        if (!token || token.length < 10) {
            return _invalidateSession();
        }

        // Decode token
        const tokenData = jwtUtils.decodeJwt(token);

        // Check if expired
        clearTimeout(invalidateSessionTimeout);
        const expiresInMilliseconds = parseInt(tokenData.exp + "000", 10) - new Date().getTime();
        console.log("[TV2] Token expires in: ", Math.round(expiresInMilliseconds / 1000), "seconds");

        // If token is expired, invalidate session
        if (expiresInMilliseconds < 1) {
            return _invalidateSession();
        }

        // ... else set timeout to invalidate session
        else {
            invalidateSessionTimeout = setTimeout(() => {
                _invalidateSession();
            }, expiresInMilliseconds);
        }

        // Set token in storage
        authUtils.setToken(token);

        // Trigger login callback
        onLogin && onLogin(token, tokenData);

        // Update state
        setToken(token);
        setData(tokenData);
    };

    /**
     * Invalidate session
     * @private
     */
    const _invalidateSession = () => {
        // Delete token
        authUtils.deleteToken();

        // Update state
        setToken(null);
        setData(null);

        console.log("[TV2] Session invalidated.");

        // Redirect to login form
        const url = new URL(window.location.href);
        window.location.href = `${loginUrl}?appId=${appId}&callback=${url.toString()}`;
    };

    return (
        <AuthContext.Provider
            value={{
                token: token,
                data: data,
                setToken: _setToken,
                logout: _invalidateSession
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    /**
     * The url to redirect to when not authenticated.
     */
    loginUrl: PropTypes.string,
    /**
     * You must supply a valid app ID for the login to work.
     */
    appId: PropTypes.string,
    /**
     * Takes a function which receives the token and the parsed user data within that token.
     */
    onLogin: PropTypes.func
};
